import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, BookOpen, ExternalLink, Search } from "lucide-react";
import { useState } from "react";
import { Link, useParams } from "react-router";
import { ARTICLES, ARTICLE_BY_SLUG, HELP_LEVELS, loadHelp, type HelpArticle, type HelpLevel } from "../help";
import { helpMessages } from "../help/help.i18n";
import { Markdown, headings, plainText } from "../help/markdown";
import { useLocale, useT } from "../i18n/core";
import { ErrorState, LoadingRows, PageHead, Panel } from "../ui/Common";
import { Pill } from "../ui/Pills";

// Central de ajuda (ADR 0027): lista por nível com busca, e cada artigo com
// sumário lateral, botão para a tela que ele explica e leitura sugerida.

const levelTone: Record<HelpLevel, "ok" | "info" | "warn"> = { basic: "ok", intermediate: "info", advanced: "warn" };

/** Minúsculas e sem acento, para a busca aceitar "politica" e "Política". */
export function fold(s: string): string {
  return s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase();
}

function useHelpContent() {
  const locale = useLocale();
  return useQuery({ queryKey: ["help", locale], queryFn: () => loadHelp(locale), staleTime: Infinity });
}

function ArticleCard({ a, base }: { a: HelpArticle; base: string }) {
  const t = useT(helpMessages);
  const meta = t.articles[a.slug];
  return (
    <li>
      <Link className="help-card" to={`${base}/help/${a.slug}`}>
        <strong>{meta?.title ?? a.slug}</strong>
        <span>{meta?.summary}</span>
      </Link>
    </li>
  );
}

export function HelpPage() {
  const { orgId = "" } = useParams();
  const t = useT(helpMessages);
  const content = useHelpContent();
  const [q, setQ] = useState("");
  const base = `/o/${orgId}`;

  const needle = fold(q.trim());
  const matches = needle
    ? ARTICLES.filter((a) => {
        const meta = t.articles[a.slug];
        const hay = fold(`${meta?.title ?? ""} ${meta?.summary ?? ""} ${plainText(content.data?.[a.slug] ?? "")}`);
        return needle.split(/\s+/).every((w) => hay.includes(w));
      })
    : null;

  return (
    <>
      <PageHead title={t.title} lede={t.lede} />

      <div className="help-search">
        <Search aria-hidden="true" />
        <input type="search" value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.searchPlaceholder} aria-label={t.search} />
      </div>

      {content.isError && <ErrorState error={content.error} />}

      {matches ? (
        <Panel title={matches.length ? t.results(matches.length) : undefined} bodyless>
          {matches.length === 0 ? (
            <div className="empty">{t.noResults(q.trim())}</div>
          ) : (
            <ul className="help-list">{matches.map((a) => <ArticleCard key={a.slug} a={a} base={base} />)}</ul>
          )}
        </Panel>
      ) : (
        HELP_LEVELS.map((level) => (
          <Panel
            key={level}
            title={<><Pill tone={levelTone[level]}>{t.level[level]}</Pill></>}
            sub={t.levelSub[level]}
            bodyless
          >
            <ul className="help-list">{ARTICLES.filter((a) => a.level === level).map((a) => <ArticleCard key={a.slug} a={a} base={base} />)}</ul>
          </Panel>
        ))
      )}
    </>
  );
}

export function HelpArticlePage() {
  const { orgId = "", slug = "" } = useParams();
  const t = useT(helpMessages);
  const content = useHelpContent();
  const base = `/o/${orgId}`;
  const article = ARTICLE_BY_SLUG[slug];
  const meta = t.articles[slug];

  if (!article || !meta) {
    return (
      <>
        <PageHead title={t.title} />
        <div className="callout warn"><BookOpen aria-hidden="true" /><span>{t.notFound} <Link to={`${base}/help`}>{t.backToHelp}</Link></span></div>
      </>
    );
  }

  const body = content.data?.[slug];
  const toc = body ? headings(body) : [];
  const idx = ARTICLES.findIndex((a) => a.slug === slug);
  const prev = ARTICLES[idx - 1];
  const next = ARTICLES[idx + 1];

  return (
    <>
      <PageHead
        eyebrow={t.level[article.level]}
        title={meta.title}
        lede={meta.summary}
        actions={
          <>
            <Link className="btn" to={`${base}/help`}><ArrowLeft aria-hidden="true" /> {t.backToHelp}</Link>
            {article.route && <Link className="btn btn-primary" to={`${base}${article.route}`}><ExternalLink aria-hidden="true" /> {t.openScreen}</Link>}
          </>
        }
      />

      <div className="help-layout">
        <article className="panel help-article">
          {content.isPending && <LoadingRows rows={10} />}
          {content.isError && <div className="panel-body"><ErrorState error={content.error} /></div>}
          {body !== undefined && <Markdown source={body} base={base} />}
          {content.isSuccess && body === undefined && <div className="panel-body"><div className="callout warn"><BookOpen aria-hidden="true" /><span>{t.loadError}</span></div></div>}
          <nav className="help-pager" aria-label={t.related}>
            {prev ? <Link to={`${base}/help/${prev.slug}`}><ArrowLeft aria-hidden="true" /> {t.articles[prev.slug]?.title}</Link> : <span />}
            {next && <Link to={`${base}/help/${next.slug}`}>{t.articles[next.slug]?.title} <ArrowRight aria-hidden="true" /></Link>}
          </nav>
        </article>

        <aside className="help-side">
          {toc.length > 1 && (
            <Panel title={t.onThisPage}>
              <ol className="help-toc">{toc.map((h) => <li key={h.id}><a href={`#${h.id}`}>{h.text}</a></li>)}</ol>
            </Panel>
          )}
          <Panel title={t.related} bodyless>
            <ul className="help-list compact">
              {article.related.map((s) => ARTICLE_BY_SLUG[s] && <ArticleCard key={s} a={ARTICLE_BY_SLUG[s]} base={base} />)}
            </ul>
          </Panel>
        </aside>
      </div>
    </>
  );
}
